// Feature #288 — My Skills: the self view. Open deltas (visibility only),
// full assessment history grouped by submission, and review outcomes.
//
// Feature #309 — the "Current profile" table is gone; technical skills
// render as a strength spine. Each block carries its own heading.
// Feature #355 — the core competency radar that #309 put above the spine is
// gone too: core competencies are retired from Skills Management. The
// capability badges that used to sit beside it now have a block of their
// own, above the spine.
//
// Feature #320 — history is a change feed, and the pending-delta list is now
// the banner directly above it. The two belong together: the feed shows a
// self assessment opening a delta and NOT moving the rating, and the banner
// is where that unmoved rating is waiting to be resolved.
// Feature #326 — if somebody has asked you to assess a colleague and you
// have not done it, that is called out at the top of this page. The work
// item asks for it here specifically, and the reason is that My Skills is
// the page people actually visit: an outstanding request that only appears
// under My Assessments is invisible to anyone who does not think to look
// there, and a request nobody notices is a request nobody answers.
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types/index'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import {
  getMyAssessments,
  getPersonProfile,
  SmMyAssessmentRow,
  SmPersonProfile,
} from '@/api/sm'
import { AssessmentFeed } from './components/AssessmentFeed'
import { OpenDeltaBanner } from './components/OpenDeltaBanner'
import { CapabilityBadges } from './components/CapabilityBadges'
import { StrengthSpine } from './components/StrengthSpine'

const MySkills: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [profile, setProfile] = useState<SmPersonProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [requested, setRequested] = useState<SmMyAssessmentRow[]>([])
  // #364 — the API withholds peer assessments about the caller unless they
  // are an sm_manager or platform_admin (the same rule the backend applies,
  // server-side). This only selects the copy that explains the absence.
  const roles = user?.roles || []
  const subjectView = !(
    roles.includes(UserRole.PlatformAdmin) || roles.includes(UserRole.SmManager)
  )

  useEffect(() => {
    if (!user?.id) return
    getPersonProfile(user.id)
      .then(setProfile)
      .catch((e) => setError(e?.message || 'Failed to load your skills profile'))
    // Both kinds count as "not done yet": an untouched request, and one
    // saved as a draft and abandoned. Filtered to peer work — a draft of
    // your own self-assessment is not something anyone is waiting on.
    getMyAssessments()
      .then((rows) =>
        setRequested(rows.filter((r) => r.request_id && r.assessment_type === 'peer'))
      )
      // Silent: this is a nudge on someone else's page. Failing to fetch it
      // must not put an error banner over their own skills profile.
      .catch(() => setRequested([]))
  }, [user?.id])

  return (
    <div>
      <PageHeader
        title="My Skills"
        actions={
          <button
            onClick={() => navigate(`/sm/assess/${user?.id}`)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            New self-assessment
          </button>
        }
      />
      <div style={{ padding: '24px', display: 'grid', gap: '32px' }}>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}

        {/* Feature #326 — you have been asked, and you have not done it. */}
        {requested.length > 0 && (
          <div
            style={{
              padding: '16px 18px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              display: 'grid',
              gap: '10px',
            }}
          >
            <strong style={{ fontSize: '15px' }}>
              {requested.length === 1
                ? 'You have been asked to assess a colleague'
                : `You have been asked to assess ${requested.length} colleagues`}
            </strong>
            <div style={{ display: 'grid', gap: '8px' }}>
              {requested.map((row) => (
                <div
                  key={row.request_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '13px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ flex: 1, minWidth: '260px' }}>
                    <strong>{row.subject_display_name}</strong>
                    {row.scope_skill_names.length > 0 && (
                      <> — {row.scope_skill_names.join(', ')}</>
                    )}
                    <span style={{ color: 'var(--color-dark-grey)' }}>
                      {row.requested_by_display_name &&
                        ` · asked by ${row.requested_by_display_name}`}
                      {row.due_at && ` · due ${formatDateTime(row.due_at)}`}
                      {row.kind === 'draft' && ' · started, not submitted'}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(row.resume_url)}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: 'var(--color-magenta)',
                      color: 'var(--color-white)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                      flexShrink: 0,
                    }}
                  >
                    {row.kind === 'draft' ? 'Finish it' : 'Start'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!profile && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
        {profile && (
          <>
            {/* Feature #356 — never `canManage` here: you cannot give
                yourself a badge, whatever roles you hold. The API says the
                same with a 403. */}
            <CapabilityBadges
              capabilities={profile.capabilities}
              subjectLabel="You"
              canManage={false}
            />

            <StrengthSpine skills={profile.skills} userId={profile.user_id} />

            {/* Read-only here: the subject sees what is outstanding, an
                sm-manager convenes the review. Since #364 the API sends the
                subject only the deltas they raised themselves. */}
            <OpenDeltaBanner deltas={profile.open_deltas} />

            <AssessmentFeed
              history={profile.history}
              submissions={profile.submissions}
              reviews={profile.reviews}
              subjectView={subjectView}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default MySkills
